import { Injectable } from '@angular/core'

import { SelectedNavItem } from './left-nav-item/selected-nav-item'
import { NavComponentPrimaryItem, NavComponentItem } from './left-nav-item/nav-component-item'
import { DelayService } from './../core/delay/delay.service'
import { StyleLoaderService } from '../core/style-loader/style-loader.service'

@Injectable({providedIn: 'root'})
export class LeftNavStateService {

  // expanded items are primary nav items with secondary items; if they're expanded the drawer is open and secondary items
  // are visible
  private expandedItemIds: string[] = []

  // a selected item is an item that is actually the current route/url (it appears blue to the user)
  private selectedItemId: string

  // if the selected item is a secondary item, the 'selectedItemParentId` is its primary item
  private selectedItemParentId: string

  // on mobile, we need to animate the collapse of a primary drawer in a way that is different than tablet/desktop
  // we'll keep track of what item that is here.
  // note that this is really on relevant on mobile (other screen sizes can just utilize a basic css transition
  // and this isn't necessary)
  private collapsingItemId: string

  // When items collapse on mobile, we need to delay for a CSS transition;
  // This is the CSS variable value (in ms)
  public collapseTransitionDuration: number

  constructor(private delayService: DelayService, private styleLoaderService: StyleLoaderService) {
    // noop
  }

  public getSelectedItemId(): string {
    return (this.selectedItemId) ? this.selectedItemId : undefined
  }

  public expandItem(domId: string, isMobile: boolean): void {

    if (isMobile) {
      // mobile can only have one open at a time:
      this.expandToOnlyItem(domId)
      return
    }

    // all other screen sizes can open more than one primary nav item/drawer at once:
    this.expandedItemIds.push(domId)

  }

  public async collapseItem(domId: string, isMobile: boolean): Promise<void> {

    const idIndex = this.expandedItemIds.indexOf(domId)

    if (idIndex === -1) {
      return
    }

    if (isMobile) {
      return this.collapseItemOnMobile(domId)
    }

    this.expandedItemIds.splice(idIndex, 1)

  }

  public async collapseItemOnMobile(domId: string): Promise<void> {

    this.collapsingItemId = domId
    this.initCollapseTransitionDuration()
    this.collapseAllItems()

    await this.delayService.delay(this.collapseTransitionDuration)
    this.collapsingItemId = undefined

  }

  public expandToOnlyItem(domId: string): void {
    this.expandedItemIds = [domId]
  }

  public collapseAllItems(): void {
    this.expandedItemIds = []
  }

  public selectItem(item: SelectedNavItem): void {

    if (!item) {
      return
    }

    this.selectedItemId = item.itemId
    this.selectedItemParentId = (item.parentId) ? item.parentId : undefined
    this.expandSelectedParentOnly()

  }

  public expandSelectedParentOnly(): void {

    if (this.selectedItemParentId) {
      this.expandToOnlyItem(this.selectedItemParentId)
    } else {
      this.collapseAllItems()
    }

  }

  public someItemIsSelected(): boolean {
    return this.selectedItemId !== undefined
  }

  public someItemIsExpanded(): boolean {
    return this.expandedItemIds && this.expandedItemIds.length > 0
  }

  public someItemIsCollapsing(): boolean {
    return this.collapsingItemId !== undefined
  }

  public moreThanOneItemIsExpanded(): boolean {
    return this.expandedItemIds && this.expandedItemIds.length > 1
  }

  public itemIsCollapsing(item: NavComponentPrimaryItem): boolean {
    return this.collapsingItemId === item.domId
  }

  public itemIsSelectedParent(item: NavComponentPrimaryItem): boolean {
    return this.selectedItemParentId === item.domId
  }

  public itemIsExpanded(item: NavComponentPrimaryItem): boolean {
    return (this.expandedItemIds.indexOf(item.domId) !== -1)
  }

  public itemIsSelected(item: NavComponentItem): boolean {
    return this.selectedItemId === item.domId
  }

  public primaryItemHasLastSecondaryItemSelected(item: NavComponentPrimaryItem): boolean {

    // this isn't the selected item or it doesn't have secondary items
    if (this.selectedItemParentId !== item.domId || !item.hasValidSecondaryItems) {
      return false
    }

    // check to see if the last secondary item is selected
    return this.itemIsSelected(item.secondaryItems[item.secondaryItems.length - 1])

  }

  private initCollapseTransitionDuration(): void {

    if (this.collapseTransitionDuration) {
      return
    }

    this.collapseTransitionDuration = this.styleLoaderService.getVariable('se-left-nav-transition-duration').value

  }

}
